import { useMemo } from 'react'

export const DOTS = '...'

const range = (start: number, end: number) => {
  let length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

interface PaginationHookProps {
  totalPageCount: number
  pageSize: number
  siblings?: number
  boundaries?: number
  currentPage: number
}

export const usePagination = (props: PaginationHookProps) => {
  const { totalPageCount, pageSize, siblings = 2, currentPage, boundaries = 1 } = props
  const _total = Math.max(Math.trunc(totalPageCount), 0)

  return useMemo((): (number | string)[] => {
    const totalPageNumbers = siblings * 2 + 3 + boundaries * 2
    if (totalPageNumbers >= _total) {
      return range(1, _total)
    }

    const leftSiblingIndex = Math.max(currentPage - siblings, boundaries)
    const rightSiblingIndex = Math.min(currentPage + siblings, _total - boundaries)

    const shouldShowLeftDots = leftSiblingIndex > boundaries + 2
    const shouldShowRightDots = rightSiblingIndex < _total - (boundaries + 1)

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = siblings * 2 + boundaries + 2

      return [...range(1, leftItemCount), DOTS, ...range(_total - (boundaries - 1), _total)]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = boundaries + 1 + 2 * siblings

      return [...range(1, boundaries), DOTS, ...range(_total - rightItemCount, _total)]
    }

    return [
      ...range(1, boundaries),
      DOTS,
      ...range(leftSiblingIndex, rightSiblingIndex),
      DOTS,
      ...range(_total - boundaries + 1, _total),
    ]
  }, [_total, siblings, currentPage])
}
